import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import { logger } from '@/utils/logger'

import { ApiTaCt110 } from '@/endpoints/ApiTaCt110'
import { ApiTaCt120 } from '@/endpoints/ApiTaCt120'
import { ApiTaCt130 } from '@/endpoints/ApiTaCt130'
import { ApiTaCt140 } from '@/endpoints/ApiTaCt140'

import { convertRawValue } from '@/utils/CustomEnum'
import {
  answerSheetContentType,
  AnswerSheetContentType,
  answerViewPhase,
  AnswerViewPhase,
  layoutPattern,
  LayoutPattern,
  PositionNumber,
} from '@/modules/ink/constants'

// State types

type HandwriteTemporaryState = {
  uimData: string // base64 encoded UIM data
}
const getEmptyHandwriteTemporaryState = (): HandwriteTemporaryState => ({
  uimData: '',
})

export type AnswerSheetContent = {
  answerSheetContentId: string
  answerSheetContentName: string
  answerSheetContentUrl: string
  contentType: AnswerSheetContentType
  answerViewPhaseId: AnswerViewPhase
  layoutPatternId: LayoutPattern
  positionNum: PositionNumber
}

type AnswerSheetContentsState = {
  answerId: string // 答案 ID
  contents: AnswerSheetContent[]
}
const getEmptyAnswerSheetContentsState = (): AnswerSheetContentsState => ({
  answerId: '',
  contents: [],
})

// State action parameter

type ActionParamsFetchHandwriteTemporary = {
  answerId: string // 答案 ID
}

type ActionParamsSaveHandwrite = {
  answerId: string // 答案 ID
  uimData: string // base64 encoded UIM data
  dataCategory: 'temporary' | 'submitted'
}

type ActionParamsSaveHandwrittenToan = {
  answerId: string // 答案 ID
  imageData: string // base64 encoded toan PNG data
}

type ActionParamsFetchAnswerSheetContents = {
  pieceId: string // 学習コマ ID
  unitId: string // 配信単位 ID
}

@Module({
  stateFactory: true,
  namespaced: true,
  name: 'apis/FullDigital',
})
export default class FullDigital extends VuexModule {
  private handwriteTemporary_: HandwriteTemporaryState = getEmptyHandwriteTemporaryState()
  private answerSheetContents_: AnswerSheetContentsState = getEmptyAnswerSheetContentsState()

  get handwriteTemporary(): HandwriteTemporaryState {
    return this.handwriteTemporary_
  }

  get answerSheetContents(): AnswerSheetContentsState {
    return this.answerSheetContents_
  }

  @Mutation
  updateHandwriteTemporary(handwriteTemporary: HandwriteTemporaryState): void {
    this.handwriteTemporary_ = handwriteTemporary
  }

  @Mutation
  updateAnswerSheetContents(answerSheetContents: AnswerSheetContentsState): void {
    this.answerSheetContents_ = answerSheetContents
  }

  @Action
  clear() {
    this.context.commit('updateHandwriteTemporary', getEmptyHandwriteTemporaryState())
    this.context.commit('updateAnswerSheetContents', getEmptyAnswerSheetContentsState())
  }

  @Action
  async fetchHandwriteTemporary(params: ActionParamsFetchHandwriteTemporary): Promise<Error | undefined> {
    logger.trace('in: fetchHandwriteTemporary')

    // API: API_TA_CT_110
    const api = new ApiTaCt110.Api()
    const result = await api.get<HandwriteTemporaryState | null>(
      { query: { answerId: params.answerId } },
      // success
      (data, _) => {
        return {
          uimData: data.uimData,
        }
      },
      // error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )

    if (result === null) {
      return new Error('Failed to fetch handwrite temporary')
    }
    this.context.commit('updateHandwriteTemporary', result)
  }

  @Action
  async saveHandwrite(params: ActionParamsSaveHandwrite): Promise<Error | undefined> {
    logger.trace('in: saveHandwrite')

    // API: API_TA_CT_120
    const api = new ApiTaCt120.Api()
    const result = await api.post<undefined | null>(
      { body: { answerId: params.answerId, uimData: params.uimData, dataCategory: params.dataCategory } },
      // success
      (_, __) => {
        return undefined
      },
      // error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )

    if (result === null) {
      return new Error('Failed to save handwrite')
    }
  }

  @Action
  async saveHandwrittenToan(params: ActionParamsSaveHandwrittenToan): Promise<Error | undefined> {
    logger.trace('in: saveHandwrittenToan')

    // API: API_TA_CT_130
    const api = new ApiTaCt130.Api()
    const result = await api.post<undefined | null>(
      { body: { answerId: params.answerId, imageData: params.imageData } },
      // success
      (_, __) => {
        return undefined
      },
      // error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )

    if (result === null) {
      return new Error('Failed to save handwritten toan')
    }
  }

  @Action
  async fetchAnswerSheetContents(params: ActionParamsFetchAnswerSheetContents): Promise<Error | undefined> {
    logger.trace('in: fetchAnswerSheetContents')

    // API: API_TA_CT_140
    const api = new ApiTaCt140.Api()
    const result = await api.get<AnswerSheetContentsState | null>(
      { query: { pieceId: params.pieceId, unitId: params.unitId } },
      // success
      (data, _) => {
        return {
          answerId: data.answerId,
          contents: ((response: ApiTaCt140.Get.Response): AnswerSheetContent[] =>
            response.contents.map(c => ({
              answerSheetContentId: c.answerSheetContentId,
              answerSheetContentName: c.answerSheetContentName,
              answerSheetContentUrl: c.answerSheetContentUrl,
              contentType: convertRawValue(c.contentType, answerSheetContentType),
              answerViewPhaseId: convertRawValue(c.answerViewPhaseId, answerViewPhase),
              layoutPatternId: convertRawValue(c.layoutPatternId, layoutPattern),
              positionNum: c.positionNum,
            })))(data),
        }
      },
      // error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )

    if (result === null) {
      return new Error('Failed to fetch answer sheet contents')
    }
    this.context.commit('updateAnswerSheetContents', result)
  }
}
